import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Caption, Label } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { VoiceCallTranscriptEntry } from '../types';

interface TranscriptPanelProps {
  entries: VoiceCallTranscriptEntry[];
  mode: 'live' | 'replay';
  onBubblePress?: (entry: VoiceCallTranscriptEntry, index: number) => void;
}

function formatMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function TranscriptBubble({
  entry,
  index,
  mode,
  onPress,
}: {
  entry: VoiceCallTranscriptEntry;
  index: number;
  mode: 'live' | 'replay';
  onPress?: (entry: VoiceCallTranscriptEntry, index: number) => void;
}) {
  const { t } = useTranslation();
  const isAgent = entry.speaker === 'agent';
  const speakerName = isAgent
    ? t('voiceAgents.transcript.agent')
    : t('voiceAgents.transcript.lead');

  const isInteractive = mode === 'replay' && Boolean(onPress);
  const tsLabel = mode === 'replay' && entry.startedAtMs != null ? formatMs(entry.startedAtMs) : '';

  const bubbleContent = (
    <View style={[styles.bubbleWrapper, isAgent ? styles.agentWrapper : styles.leadWrapper]}>
      <Label
        style={[styles.speakerLabel, isAgent ? styles.agentLabel : styles.leadLabel]}
        accessibilityElementsHidden={isInteractive}
        importantForAccessibility={isInteractive ? 'no' : 'auto'}
      >
        {speakerName}
        {tsLabel ? `  ${tsLabel}` : ''}
      </Label>
      <View style={[styles.bubble, isAgent ? styles.agentBubble : styles.leadBubble]}>
        <Label
          style={[styles.bubbleText, isAgent ? styles.agentText : styles.leadText]}
          accessibilityElementsHidden={isInteractive}
          importantForAccessibility={isInteractive ? 'no' : 'auto'}
          accessibilityRole={isInteractive ? undefined : 'text'}
          accessibilityLabel={isInteractive ? undefined : `${speakerName}: ${entry.text}`}
        >
          {entry.text}
        </Label>
      </View>
    </View>
  );

  if (isInteractive) {
    return (
      <Pressable
        onPress={() => onPress?.(entry, index)}
        hitSlop={{ top: 6, bottom: 6, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={`${t('voiceAgents.transcript.turn')} ${entry.turnIndex}: ${speakerName}${tsLabel ? ` ${t('voiceAgents.transcript.at')} ${tsLabel}` : ''}: ${entry.text}`}
        accessibilityHint={t('voiceAgents.transcript.seekHint')}
        style={({ pressed }) => pressed && styles.pressed}
      >
        {bubbleContent}
      </Pressable>
    );
  }

  return bubbleContent;
}

export function TranscriptPanel({ entries, mode, onBubblePress }: TranscriptPanelProps) {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return (
      <View
        style={styles.empty}
        accessible
        accessibilityLabel={t('voiceAgents.transcript.emptyLabel')}
      >
        <Caption style={styles.emptyText}>{t('voiceAgents.transcript.empty')}</Caption>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      accessibilityLabel={t('voiceAgents.transcript.callTranscript')}
      accessibilityLiveRegion={mode === 'live' ? 'polite' : 'none'}
    >
      {entries.map((entry, index) => (
        <TranscriptBubble
          key={entry.id}
          entry={entry}
          index={index}
          mode={mode}
          onPress={onBubblePress}
        />
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 12,
  },
  bubbleWrapper: {
    maxWidth: '80%',
    gap: 4,
  },
  agentWrapper: {
    alignSelf: 'flex-start',
  },
  leadWrapper: {
    alignSelf: 'flex-end',
  },
  speakerLabel: {
    fontSize: 10,
    color: colors.text.tertiary,
  },
  agentLabel: {
    textAlign: 'left',
  },
  leadLabel: {
    textAlign: 'right',
  },
  bubble: {
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  agentBubble: {
    backgroundColor: colors.primary.muted,
    borderBottomLeftRadius: 4,
  },
  leadBubble: {
    backgroundColor: colors.surface.row,
    borderBottomRightRadius: 4,
  },
  bubbleText: {
    fontSize: 14,
    lineHeight: 20,
  },
  agentText: {
    color: colors.primary[200],
  },
  leadText: {
    color: colors.text.primary,
  },
  pressed: {
    opacity: 0.75,
  },
  empty: {
    padding: 24,
    alignItems: 'center',
  },
  emptyText: {
    color: colors.text.tertiary,
  },
});
